import { VirtualItem, Virtualizer, elementScroll, observeElementOffset, observeElementRect } from "@tanstack/virtual-core";

export type { VirtualItem };

export interface VirtualScrollerOptions<T> {
  items: T[];
  // Per-index height estimate. Must be a pure function of the item data (no
  // virtualizer mutation, no render scheduling) so it can't re-enter the
  // measure cycle. Callers with uniform rows can return a constant.
  estimateItemHeight: (index: number) => number;
  overscan?: number;
  measureElement?: (element: HTMLElement) => number;
}

/**
 * Default element measurement. Unlike `getBoundingClientRect().height`, this
 * also accounts for the vertical margins of the item so the virtualizer's
 * cumulative offsets stay aligned with the real document flow (the visible
 * items are laid out with `margin-bottom` between them). Without this, every
 * item would be measured slightly shorter than it actually occupies, and the
 * error accumulates the further you scroll.
 */
const measureElementWithMargins = (element: HTMLElement): number => {
  const height = element.getBoundingClientRect().height;
  const style = window.getComputedStyle(element);
  const marginTop = Number.parseFloat(style.marginTop) || 0;
  const marginBottom = Number.parseFloat(style.marginBottom) || 0;
  return Math.round(height + marginTop + marginBottom);
};

export interface VirtualScrollerState {
  virtualItems: VirtualItem[];
  totalHeight: number;
}

export class VirtualScroller<T = any> {
  private _virtualizer?: Virtualizer<HTMLElement, HTMLElement>;
  private _viewport?: HTMLElement;
  private _cleanup?: () => void;
  private _options: VirtualScrollerOptions<T>;

  constructor(options: VirtualScrollerOptions<T>) {
    this._options = options;
  }

  setup(viewport: HTMLElement, onStateChange: (state: VirtualScrollerState) => void): () => void {
    this._viewport = viewport;

    this._virtualizer = new Virtualizer<HTMLElement, HTMLElement>({
      count: this._options.items.length,
      getScrollElement: () => this._viewport ?? null,
      estimateSize: (index) => this._options.estimateItemHeight(index),
      overscan: this._options.overscan ?? 5,
      measureElement: (element) => (this._options.measureElement ?? measureElementWithMargins)(element as HTMLElement),
      observeElementRect,
      observeElementOffset,
      scrollToFn: elementScroll,
      onChange: () => {
        onStateChange(this.getState());
      },
    });

    this._cleanup = this._virtualizer._didMount();
    this._virtualizer._willUpdate();
    onStateChange(this.getState());

    return () => {
      this._cleanup?.();
    };
  }

  willUpdate() {
    this._virtualizer?._willUpdate();
  }

  updateItems(items: T[], onStateChange?: (state: VirtualScrollerState) => void) {
    this._options.items = items;
    if (this._virtualizer) {
      this._virtualizer.setOptions({
        ...this._virtualizer.options,
        count: items.length,
      });
      this._virtualizer._willUpdate();
    }
    if (onStateChange) {
      onStateChange(this.getState());
    }
  }

  measureElement(el: HTMLElement) {
    this._virtualizer?.measureElement(el);
  }

  scrollToIndex(index: number, behavior: ScrollBehavior = "smooth", align: "auto" | "start" | "center" | "end" = "auto") {
    this._virtualizer?.scrollToIndex(index, { behavior, align });
  }

  /**
   * Estimated `start` offset (px) of an item, using real measurements where
   * available and the size estimate for not-yet-rendered items. Used to jump
   * the viewport near a target before a precise DOM-based alignment, instead of
   * `scrollToIndex` (whose reconcile re-scrolls every frame and froze the UI).
   */
  getOffsetForIndex(index: number): number {
    return this._virtualizer?.getOffsetForIndex(index, "start")?.[0] ?? 0;
  }

  getState(): VirtualScrollerState {
    return {
      virtualItems: this._virtualizer?.getVirtualItems() ?? [],
      totalHeight: this._virtualizer?.getTotalSize() ?? 0,
    };
  }
}
